import React, { useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import clsx from 'clsx';

import useFileUploader from '@ecp/common/src/components/unstyled/input/useFileUploader';
import {nanoid} from "nanoid";

export const FILE_EXTENSION = {
  ALL: [],
  ARCHIVE: ['zip', '7z', 'alz', 'egg'],
  DOCUMENT: ['xls', 'xlsx', 'ppt', 'pptx', 'doc', 'docx', 'pdf'],
  IMAGE: ['jpg', 'jpeg', 'png', 'gif', 'svg'],
  VIDEO: ['mp4', 'wmv', 'asf', 'flv', 'mov', 'mpeg'],
  ICO: ['ico'],
};

export const FILE_SIZE = {
  DEFAULT: 50 * 1024 * 1024,
  IMAGE: 20 * 1024 * 1024,
};

export const fileUploaderClassNames = {
  disabled: 'fileUploader-disabled',
};

const Input = styled.input`
  display: none;
  position: absolute;
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  height: 100%;
  margin: 0px;
  padding: 0px;
`;

const Label = styled.label`
  line-height: 0;
  cursor: pointer;
  //position: relative;
`;

const FileUploaderUnstyled = React.forwardRef(
  (
    {
      value,
      rootComponent: RootComponent,
      dropzoneComponent: DropzoneComponent,
      buttonComponent: ButtonComponent,
      guideComponent: GuideComponent,
      ...props
    },
    ref
  ) => {
    const fileUploaderRef = useRef(null);

    const { drop, getDropzoneProps, getInputProps } = useFileUploader(props);

    const stateClasses = {
      [fileUploaderClassNames.disabled]: props.disabled,
    };
    
    const fileInputId = `photo_${nanoid()}`;

    useImperativeHandle(ref, () => fileUploaderRef.current);

    return (
      <RootComponent>
        <DropzoneComponent ref={drop} {...getDropzoneProps()}>
          <Label {...props} className={clsx(stateClasses)} htmlFor={fileInputId}>
            <Input value={value} className={clsx(stateClasses)} ref={fileUploaderRef} {...getInputProps()} id={fileInputId} title="이미지 추가"/>
            {ButtonComponent && typeof ButtonComponent !== 'string' ? (
              React.cloneElement(ButtonComponent, { className: clsx(stateClasses) })
            ) : (
              <span>{ButtonComponent}</span>
            )}
          </Label>
          {GuideComponent}
        </DropzoneComponent>
      </RootComponent>
    );
  }
);

FileUploaderUnstyled.displayName = 'fileUploader';

FileUploaderUnstyled.propTypes = {
  disabled: PropTypes.bool,
  value: PropTypes.any,
  onChange: PropTypes.func,
  className: PropTypes.string,
  rootComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element, PropTypes.string]),
  dropzoneComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.string]),
  buttonComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element, PropTypes.string]),
  dragdrop: PropTypes.bool,
  multiple: PropTypes.bool,
  maxCount: PropTypes.number,
  maxSize: PropTypes.number,
  maxTotalSize: PropTypes.number,
  extension: PropTypes.arrayOf(PropTypes.string),
  guideComponent: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
};

FileUploaderUnstyled.defaultProps = {
  rootComponent: 'div',
  dropzoneComponent: 'div',
  buttonComponent: 'div',
  guideComponent: '',
  dragdrop: false,
  multiple: false,
  maxCount: 1,
  maxSize: FILE_SIZE.DEFAULT,
  maxTotalSize: 0,
  extension: FILE_EXTENSION.ALL,
};

export default FileUploaderUnstyled;
